import { useEffect, useState } from 'react';
import { addressService } from '../../services/addressService';
import { locationService } from '../../services/locationService';
import './AddressManager.css';

const blankForm = {
  label: '',
  full_name: '',
  phone: '',
  address: '',
  city: '',
  province_id: '',
  district_id: '',
  is_default: false,
};

const AddressManager = () => {
  const [addresses, setAddresses] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(blankForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = () =>
    addressService.list().then((list) => {
      setAddresses(list);
      setLoading(false);
    });

  useEffect(() => {
    load().catch(() => setLoading(false));
    locationService.getProvinces().then(setProvinces).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.province_id) {
      setDistricts([]);
      return;
    }
    locationService.getDistricts(form.province_id).then(setDistricts).catch(() => setDistricts([]));
  }, [form.province_id]);

  const startNew = () => {
    setForm(blankForm);
    setEditing('new');
    setError(null);
  };

  const startEdit = (addr) => {
    setForm({
      label: addr.label || '',
      full_name: addr.full_name || '',
      phone: addr.phone || '',
      address: addr.address || '',
      city: addr.city || '',
      province_id: addr.province_id || '',
      district_id: addr.district_id || '',
      is_default: addr.is_default || false,
    });
    setEditing(addr.id);
    setError(null);
  };

  const cancel = () => {
    setEditing(null);
    setError(null);
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => {
      const next = { ...f, [field]: value };
      if (field === 'province_id') next.district_id = '';
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      const payload = { ...form };
      if (!payload.label) delete payload.label;
      if (!payload.city) delete payload.city;
      if (!payload.province_id) delete payload.province_id;
      if (!payload.district_id) delete payload.district_id;

      if (editing === 'new') {
        await addressService.create(payload);
      } else {
        await addressService.update(editing, payload);
      }
      await load();
      setEditing(null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await addressService.remove(id);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to delete address');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await addressService.setDefault(id);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update default');
    }
  };

  if (loading) return <p>Loading addresses…</p>;

  return (
    <div className="address-manager">
      <div className="address-manager-header">
        <h3>Saved Addresses</h3>
        {editing === null && (
          <button className="btn-add-address" onClick={startNew}>+ Add new address</button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {editing !== null && (
        <form className="address-form" onSubmit={submit}>
          <div className="form-row">
            <div className="form-group">
              <label>Label (e.g. Home, Office)</label>
              <input value={form.label} onChange={handleChange('label')} />
            </div>
            <div className="form-group">
              <label>Full Name *</label>
              <input value={form.full_name} onChange={handleChange('full_name')} required />
            </div>
          </div>

          <div className="form-group">
            <label>Phone *</label>
            <input
              value={form.phone}
              onChange={handleChange('phone')}
              pattern="[0-9]{10}"
              title="10 digits"
              required
            />
          </div>

          <div className="form-group">
            <label>Street Address / Locality *</label>
            <textarea value={form.address} onChange={handleChange('address')} required />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Province *</label>
              <select value={form.province_id} onChange={handleChange('province_id')} required>
                <option value="">Select province</option>
                {provinces.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>District *</label>
              <select
                value={form.district_id}
                onChange={handleChange('district_id')}
                disabled={!form.province_id}
                required
              >
                <option value="">{form.province_id ? 'Select district' : 'Pick province first'}</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>City / Town</label>
            <input value={form.city} onChange={handleChange('city')} />
          </div>

          <div className="form-group form-checkbox">
            <label>
              <input
                type="checkbox"
                checked={form.is_default}
                onChange={handleChange('is_default')}
              />
              Make this my default address
            </label>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={cancel}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? 'Saving…' : 'Save Address'}
            </button>
          </div>
        </form>
      )}

      {addresses.length === 0 && editing === null && (
        <p className="address-empty">You don&apos;t have any saved addresses yet.</p>
      )}

      <div className="address-list">
        {addresses.map((addr) => (
          <div key={addr.id} className={`address-card ${addr.is_default ? 'is-default' : ''}`}>
            <div className="address-card-header">
              <div>
                {addr.label && <span className="address-label">{addr.label}</span>}
                {addr.is_default && <span className="address-default-badge">Default</span>}
              </div>
            </div>
            <div className="address-card-body">
              <p className="address-name">{addr.full_name}</p>
              <p>{addr.address}</p>
              <p>
                {[addr.city, addr.district?.name, addr.province?.name].filter(Boolean).join(', ')}
              </p>
              <p>{addr.phone}</p>
            </div>
            <div className="address-card-actions">
              {!addr.is_default && (
                <button onClick={() => handleSetDefault(addr.id)}>Set as default</button>
              )}
              <button onClick={() => startEdit(addr)}>Edit</button>
              <button className="danger" onClick={() => handleDelete(addr.id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AddressManager;
